import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import "katex/dist/katex.min.css";
import PropTypes from "prop-types";
import MarkdownEditorPopup from "./MarkdownEditorPopup";
import ContentGeneratorPopup from "./ContentGeneratorPopup";
import ImageViewer from "./ImageViewer";
import "../../styles/common/markdownViewer.scss";

const MarkdownViewer = ({
  content,
  onEdit,
  onAdd,
  onUpdate,
  pageId,
  pageData,
  context,
  courseKey,
  subjectKey,
  topicKey,
  subTopicKey,
}) => {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);

  let displayContent = "";

  if (typeof content === "string") {
    try {
      const parsedContent = JSON.parse(content);
      if (Array.isArray(parsedContent)) {
        displayContent = parsedContent
          .map((item) => item.content || "")
          .join("\n\n");
      } else if (
        parsedContent &&
        typeof parsedContent === "object" &&
        parsedContent.content
      ) {
        displayContent = parsedContent.content;
      } else {
        displayContent = content;
      }
    } catch (e) {
      displayContent = content;
    }
  } else if (Array.isArray(content)) {
    displayContent = content
      .map((item) => {
        if (typeof item === "string") {
          return item;
        } else if (item && typeof item === "object") {
          if (item.content) {
            if (typeof item.content === "string") {
              return item.content;
            } else {
              try {
                return JSON.stringify(item.content, null, 2);
              } catch (e) {
                return String(item.content);
              }
            }
          } else {
            try {
              return JSON.stringify(item, null, 2);
            } catch (e) {
              return String(item);
            }
          }
        }
        return String(item);
      })
      .join("\n\n");
  } else if (content && typeof content === "object") {
    if (Array.isArray(content.content)) {
      displayContent = content.content
        .map((item) => {
          if (typeof item === "string") {
            return item;
          } else if (item && typeof item === "object") {
            if (item.content) {
              if (typeof item.content === "string") {
                return item.content;
              } else {
                try {
                  return JSON.stringify(item.content, null, 2);
                } catch (e) {
                  return String(item.content);
                }
              }
            } else {
              try {
                return JSON.stringify(item, null, 2);
              } catch (e) {
                return String(item);
              }
            }
          }
          return String(item);
        })
        .join("\n\n");
    } else if (content.content && typeof content.content === "string") {
      displayContent = content.content;
    } else if (content.content) {
      try {
        displayContent = JSON.stringify(content.content, null, 2);
      } catch (e) {
        displayContent = String(content.content);
      }
    }
  }

  if (displayContent === "" && (content === null || content === undefined)) {
    displayContent = "No content available";
  }

  const handleSaveGeneratedContent = (updatedPageData) => {
    if (onUpdate) {
      onUpdate(updatedPageData);
    }
  };

  return (
    <div className="markdown-viewer">
      {displayContent ? (
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[rehypeKatex]}
          components={{
            code({ node, inline, className, children, ...props }) {
              const match = /language-(\w+)/.exec(className || "");

              if (!inline && match && match[1] === "json") {
                const raw = String(children).trim();
                try {
                  const parsed = JSON.parse(raw);
                  if (
                    Array.isArray(parsed) &&
                    parsed.every(
                      (item) =>
                        item &&
                        typeof item === "object" &&
                        item.type === "image" &&
                        typeof item.url === "string"
                    )
                  ) {
                    return <ImageViewer images={parsed} />;
                  }
                } catch (e) {}
              }

              return !inline && match ? (
                <SyntaxHighlighter
                  style={oneDark}
                  language={match[1]}
                  PreTag="div"
                  {...props}
                >
                  {String(children).replace(/\n$/, "")}
                </SyntaxHighlighter>
              ) : (
                <code className={className} {...props}>
                  {children}
                </code>
              );
            },
          }}
        >
          {displayContent}
        </ReactMarkdown>
      ) : (
        <div className="no-content">No content available</div>
      )}

     {isEditorOpen&& <MarkdownEditorPopup
        open={isEditorOpen}
        handleClose={() => setIsEditorOpen(false)}
        title="Edit Content"
        initialValue={content}
        onSave={(updatedPageData) => {
          if (onUpdate) onUpdate(updatedPageData);
          setIsEditorOpen(false);
        }}
        context={context}
        pageId={pageId}
        pageData={pageData}
        courseKey={courseKey}
        subjectKey={subjectKey}
        topicKey={topicKey}
        subTopicKey={subTopicKey}
      />}

     {isGeneratorOpen&& <ContentGeneratorPopup
        open={isGeneratorOpen}
        handleClose={() => setIsGeneratorOpen(false)}
        onSave={handleSaveGeneratedContent}
        existingContent={displayContent}
        pageId={pageId}
        pageData={pageData}
        context={context}
        courseKey={courseKey}
        subjectKey={subjectKey}
        topicKey={topicKey}
        subTopicKey={subTopicKey}
      />}
    </div>
  );
};

MarkdownViewer.propTypes = {
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
  onEdit: PropTypes.func,
  onAdd: PropTypes.func,
  onUpdate: PropTypes.func,
  pageId: PropTypes.string,
  pageData: PropTypes.object,
  context: PropTypes.string,
  courseKey: PropTypes.string,
  subjectKey: PropTypes.string,
  topicKey: PropTypes.string,
  subTopicKey: PropTypes.string,
};

MarkdownViewer.defaultProps = {
  content: "",
  onEdit: null,
  onAdd: null,
  onUpdate: null,
  pageId: null,
  pageData: null,
  context: null,
  courseKey: null,
  subjectKey: null,
  topicKey: null,
  subTopicKey: null,
};

export default MarkdownViewer;
